import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Card from "@mui/material/Card";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import { H6 } from "src/lib/components/salify-next-layout/default/components/Typography";
import { useGlobalContext } from "src/lib/components/salify-next-layout/default/global-context";
import clientFetcher, {
  CheckPromotionPayload,
  GetRecommendationPromotionPayload,
  PromotionResult,
  PromotionResultType,
} from "src/lib/fetcher/client";
import { toast } from "react-toastify";

const formatVnd = (n: number) => `${n.toLocaleString("vi-VN")} ₫`;

// ==============================================================
const PromotionForm = (props: {
  form?: boolean;
  values: any;
  errors: any;
  touched: any;
  handleBlur: any;
  handleChange: any;
  setFieldValue: any;
  controlOnly?: boolean;
  controlPrefix?: string;
  readOnly?: boolean;
}) => {
  const formTheme = useTheme();

  const RenderControls = () => {
    const theme = formTheme;
    const isMobile = useMediaQuery("(max-width:767px)");
    const [open, setOpen] = React.useState(false);
    const [loadingRecs, setLoadingRecs] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const { cartItems: productList, updatePromotionResult, promotionResult } = useGlobalContext();
    const [code, setCode] = useState<string>(promotionResult?.code || "");
    const [recommendations, setRecommendations] = useState<PromotionResult[]>([]);

    const checkPromotion = () => {
      if (!code || !code.length) {
        return;
      }
      const payload: CheckPromotionPayload = {
        code: code,
        productIds: productList.map((item) => item.product.id),
      };
      clientFetcher.checkPromotion(payload).then(async (result) => {
        const { error, value: response } = result;
        if (error) {
          console.error("[Order] exception", error);
          toast.error(
            "Có lỗi xảy ra khi thực hiện order, xin thực hiện tải lại trang hoặc nhấn F5 để thử lại.",
            { position: "bottom-right" },
          );
          return;
        }
        const promotion = response.promotionResults[0];
        updatePromotionResult(promotion);
      });
    };

    useEffect(() => {
      const getRecommendation = () => {
        setLoadingRecs(true);
        const payload: GetRecommendationPromotionPayload = {
          productIds: productList.map((item) => item.product.id),
        };
        clientFetcher.getRecommendationPromotion(payload).then(async (result) => {
          const { error, value: response } = result;
          if (!error) {
            let merged: PromotionResult[] = [];
            response.forEach((item) => {
              item.results.forEach((r) => {
                r.code = item.code;
              });
              merged.push(...item.results);
            });
            setRecommendations(merged);
          }
          setLoadingRecs(false);
        });
      };
      if (open && (!recommendations || !recommendations.length)) {
        getRecommendation();
      }
    }, [open]);

    const promotionToText = (promotion: PromotionResult) => {
      let result = "";
      switch (promotion.resultType) {
        case PromotionResultType.DiscountAmount:
          result = `Giảm ${formatVnd(promotion.value)} cho đơn hàng`;
          break;
        case PromotionResultType.DiscountPercentage:
          result = `Giảm ${promotion.value}% giá trị đơn hàng`;
          break;
        case PromotionResultType.FreeProduct:
          result = `Tặng kèm sản phẩm ${promotion.freeProductId}`;
          break;
        case PromotionResultType.FreeShipping:
          result = "Miễn phí vận chuyển";
          break;
      }
      return result;
    };

    const promotionIcon = (t: PromotionResultType) => {
      const c = theme.palette.primary.main;
      switch (t) {
        case PromotionResultType.DiscountAmount:
          return <AttachMoneyOutlinedIcon sx={{ color: c, fontSize: 28 }} />;
        case PromotionResultType.DiscountPercentage:
          return <PercentOutlinedIcon sx={{ color: c, fontSize: 28 }} />;
        case PromotionResultType.FreeProduct:
          return <CardGiftcardOutlinedIcon sx={{ color: c, fontSize: 28 }} />;
        case PromotionResultType.FreeShipping:
          return <LocalShippingOutlinedIcon sx={{ color: c, fontSize: 28 }} />;
        default:
          return <LocalOfferOutlinedIcon sx={{ color: c, fontSize: 28 }} />;
      }
    };

    const accent = theme.palette.primary.main;
    const btnCompactSx = {
      minHeight: 34,
      py: 0.5,
      px: 1.25,
      borderRadius: 1.5,
      fontSize: "0.8125rem",
      fontWeight: 600,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      textTransform: "none" as const,
      "& .MuiButton-startIcon": { mr: 0.5, ml: -0.25 },
      "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 18 },
    };

    return (
      <Stack spacing={2}>
        <SalifyFallback.Label
          path="Voucher Code"
          default="Mã"
          render={(label) => (
            <TextField
              fullWidth
              onBlur={props.handleBlur}
              label={label}
              placeholder="Nhập mã voucher"
              onChange={(val) => setCode(val?.target?.value || "")}
              name={"promotion_code"}
              InputProps={{
                readOnly: props.readOnly,
                startAdornment: (
                  <InputAdornment position="start">
                    <ConfirmationNumberOutlinedIcon
                      sx={{ color: alpha(accent, 0.85), fontSize: 22 }}
                    />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.03),
                  "& fieldset": { borderColor: alpha(accent, 0.2) },
                  "&:hover fieldset": { borderColor: alpha(accent, 0.4) },
                },
              }}
              value={code}
            />
          )}
        />

        <Stack direction="row" spacing={1} useFlexGap sx={{ width: "100%", alignItems: "stretch" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpen}
            startIcon={<LocalOfferOutlinedIcon />}
            sx={{
              ...btnCompactSx,
              flex: "1 1 auto",
              minWidth: 0,
              borderWidth: 1,
              borderColor: alpha(accent, 0.28),
              color: accent,
              bgcolor: theme.palette.background.paper,
              "&:hover": {
                borderWidth: 1,
                borderColor: alpha(accent, 0.55),
                bgcolor: alpha(accent, 0.06),
              },
            }}
          >
            {isMobile ? "Gợi ý" : "Xem khuyến mãi"}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={checkPromotion}
            sx={{
              ...btnCompactSx,
              flex: "0 0 auto",
              fontWeight: 700,
            }}
          >
            Áp dụng
          </Button>
        </Stack>

        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          scroll="paper"
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: alpha(theme.palette.common.black, 0.5),
                backdropFilter: "none",
              },
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              backgroundImage: "none",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8],
            },
          }}
        >
          <DialogTitle
            sx={{
              py: 2,
              px: 2.5,
              borderBottom: `1px solid ${alpha(accent, 0.1)}`,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.12),
                }}
              >
                <LocalOfferOutlinedIcon sx={{ color: accent, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h6" component="span" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                  Ưu đãi dành cho bạn
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Chọn một mã — áp dụng ngay khi thanh toán
                </Typography>
              </Box>
            </Stack>
            <IconButton
              aria-label="Đóng"
              onClick={handleClose}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 2.5, py: 2.5 }}>
            {loadingRecs ? (
              <Stack alignItems="center" justifyContent="center" py={6} spacing={2}>
                <CircularProgress size={40} thickness={4} sx={{ color: accent }} />
                <Typography color="text.secondary" variant="body2">
                  Đang tải danh sách khuyến mãi…
                </Typography>
              </Stack>
            ) : recommendations.length === 0 ? (
              <Stack alignItems="center" py={5} spacing={1.5}>
                <LocalOfferOutlinedIcon
                  sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.35) }}
                />
                <Typography color="text.secondary" textAlign="center">
                  Chưa có khuyến mãi phù hợp giỏ hàng hiện tại.
                </Typography>
                <Typography variant="caption" color="text.disabled" textAlign="center">
                  Thử nhập mã thủ công phía trên hoặc quay lại sau.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {recommendations.map((item, idx) => {
                  const selected = code === item.code;
                  return (
                    <Box
                      component="button"
                      type="button"
                      key={`${item.code}-${item.promotionCampaignDetailId ?? idx}`}
                      onClick={() => setCode(item.code)}
                      sx={{
                        all: "unset",
                        cursor: "pointer",
                        display: "block",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "stretch",
                          gap: 1.5,
                          p: 1.75,
                          borderRadius: 2.5,
                          border: "2px solid",
                          borderColor: selected ? accent : alpha(accent, 0.12),
                          bgcolor: selected ? alpha(accent, 0.08) : theme.palette.action.hover,
                          transition: "border-color 0.2s, background 0.2s, box-shadow 0.2s",
                          boxShadow: selected ? `0 4px 16px ${alpha(accent, 0.18)}` : "none",
                          "&:hover": {
                            borderColor: alpha(accent, 0.45),
                            bgcolor: alpha(accent, 0.04),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 52,
                            borderRadius: 2,
                            bgcolor: alpha(accent, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {promotionIcon(item.resultType)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              label={item.code}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                letterSpacing: 0.3,
                                fontFamily: "ui-monospace, monospace",
                                bgcolor: alpha(accent, 0.14),
                                color: accent,
                              }}
                            />
                            {selected && (
                              <Chip
                                label="Đang chọn"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 600, height: 22 }}
                              />
                            )}
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.75, fontWeight: 500, lineHeight: 1.45 }}
                          >
                            {promotionToText(item)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              px: 2.5,
              py: 2,
              borderTop: `1px solid ${alpha(accent, 0.1)}`,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button onClick={handleClose} sx={{ textTransform: "none", fontWeight: 600 }}>
              Hủy
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!recommendations.length}
              onClick={() => {
                setOpen(false);
                checkPromotion();
              }}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3 }}
            >
              Áp dụng mã đã chọn
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  };

  if (props.controlOnly) return RenderControls();

  const theme = formTheme;
  const accent = theme.palette.primary.main;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        p: { xs: 2.5, sm: 3 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        boxShadow: theme.shadows[1],
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: alpha(accent, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LocalOfferOutlinedIcon sx={{ color: accent, fontSize: 24 }} />
        </Box>
        <Box>
          <H6 mb={0} sx={{ fontWeight: 800 }}>
            <SalifyFallback.Label path="Promotion" default="Khuyến mãi" />
          </H6>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Nhập mã hoặc chọn từ gợi ý để được giá tốt nhất
          </Typography>
        </Box>
      </Stack>

      {RenderControls()}
    </Card>
  );
};

export default PromotionForm;
